import React, { createContext, useContext, useState, useEffect } from 'react';
import { transliterateToTamil } from '../utils/transliterate';
import { startTanglish, stopTanglish } from '../utils/tanglishRuntime';

const LanguageContext = createContext();

const LANGUAGES = [
  { code: 'en', label: 'English', flag: 'EN' },
  { code: 'hi', label: 'हिन्दी', flag: 'HI' },
  { code: 'ta', label: 'தமிழ்', flag: 'TA' },
];

const translations = {
  // Common
  'common.login': { en: 'Login', hi: 'लॉगिन', ta: 'உள்நுழை' },
  'common.logout': { en: 'Logout', hi: 'लॉगआउट', ta: 'வெளியேறு' },
  'common.register': { en: 'Register', hi: 'पंजीकरण', ta: 'பதிவு' },
  'common.save': { en: 'Save', hi: 'सेव करें', ta: 'சேமி' },
  'common.cancel': { en: 'Cancel', hi: 'रद्द करें', ta: 'ரத்து' },
  'common.add': { en: 'Add', hi: 'जोड़ें', ta: 'சேர்' },
  'common.edit': { en: 'Edit', hi: 'संपादित करें', ta: 'திருத்து' },
  'common.delete': { en: 'Delete', hi: 'हटाएं', ta: 'நீக்கு' },
  'common.submit': { en: 'Submit', hi: 'जमा करें', ta: 'சமர்ப்பி' },
  'common.copy': { en: 'Copy', hi: 'कॉपी', ta: 'நகலெடு' },
  'common.info': { en: 'Info', hi: 'जानकारी', ta: 'தகவல்' },
  'common.dashboard': { en: 'Dashboard', hi: 'डैशबोर्ड', ta: 'டாஷ்போர்டு' },
  'common.loading': { en: 'Loading...', hi: 'लोड हो रहा है...', ta: 'ஏற்றுகிறது...' },
  'common.no_data': { en: 'No data found', hi: 'कोई डेटा नहीं मिला', ta: 'தரவு இல்லை' },
  'common.welcome': { en: 'Welcome', hi: 'स्वागत है', ta: 'வரவேற்கிறோம்' },

  // Home
  'home.hero_title': { en: 'Plan Your Perfect Event', hi: 'अपना परफेक्ट इवेंट प्लान करें', ta: 'உங்கள் சிறந்த நிகழ்வைத் திட்டமிடுங்கள்' },
  'home.hero_subtitle': { en: 'Find and book the best vendors for your wedding, birthday, and special events', hi: 'अपनी शादी, जन्मदिन और खास इवेंट्स के लिए सबसे अच्छे वेंडर खोजें और बुक करें', ta: 'உங்கள் திருமணம், பிறந்தநாள் மற்றும் சிறப்பு நிகழ்வுகளுக்கு சிறந்த விற்பனையாளர்களைக் கண்டறிந்து முன்பதிவு செய்யுங்கள்' },
  'home.get_instant_pricing': { en: 'Get Instant Pricing', hi: 'तुरंत कीमत पाएं', ta: 'உடனடி விலை பெறுங்கள்' },
  'home.browse_vendors': { en: 'Browse Vendors', hi: 'वेंडर ब्राउज़ करें', ta: 'விற்பனையாளர்களைப் பாருங்கள்' },

  // Auth
  'auth.welcome_back': { en: 'Welcome Back', hi: 'वापस आपका स्वागत है', ta: 'மீண்டும் வரவேற்கிறோம்' },
  'auth.login_to_account': { en: 'Login to your account', hi: 'अपने अकाउंट में लॉगिन करें', ta: 'உங்கள் கணக்கில் உள்நுழையுங்கள்' },
  'auth.phone': { en: 'Phone', hi: 'फोन', ta: 'தொலைபேசி' },
  'auth.email_admin': { en: 'Email (Admin)', hi: 'ईमेल (एडमिन)', ta: 'மின்னஞ்சல் (நிர்வாகி)' },
  'auth.phone_number': { en: 'Phone Number', hi: 'फोन नंबर', ta: 'போன் நம்பர்' },
  'auth.password': { en: 'Password', hi: 'पासवर्ड', ta: 'கடவுச்சொல்' },
  'auth.no_account': { en: "Don't have an account?", hi: 'अकाउंट नहीं है?', ta: 'கணக்கு இல்லையா?' },
  'auth.register_here': { en: 'Register here', hi: 'यहां पंजीकरण करें', ta: 'இங்கே பதிவு செய்யுங்கள்' },
  'auth.create_account': { en: 'Create Account', hi: 'अकाउंट बनाएं', ta: 'கணக்கை உருவாக்கு' },
  'auth.join_eventbook': { en: 'Join BeeWeddy as a customer, vendor, or professional', hi: 'कस्टमर, वेंडर या प्रोफेशनल के रूप में BeeWeddy से जुड़ें', ta: 'வாடிக்கையாளர், விற்பனையாளர் அல்லது நிபுணராக BeeWeddy-இல் சேருங்கள்' },
  'auth.customer': { en: 'Customer', hi: 'कस्टमर', ta: 'கஸ்டமர்' },
  'auth.vendor': { en: 'Vendor', hi: 'वेंडर', ta: 'வெண்டர்' },
  'auth.professional': { en: 'Professional', hi: 'प्रोफेशनल', ta: 'நிபுணர்' },
  'auth.full_name': { en: 'Full Name', hi: 'पूरा नाम', ta: 'ஃபுல் நேம்' },
  'auth.location': { en: 'Location', hi: 'स्थान', ta: 'இடம்' },
  'auth.already_have_account': { en: 'Already have an account?', hi: 'पहले से अकाउंट है?', ta: 'ஏற்கனவே கணக்கு உள்ளதா?' },
  'auth.login_here': { en: 'Login here', hi: 'यहां लॉगिन करें', ta: 'இங்கே உள்நுழையுங்கள்' },

  // Instant Pricing
  'pricing.title': { en: 'Instant Pricing', hi: 'तुरंत कीमत', ta: 'உடனடி விலை' },
  'pricing.subtitle': { en: 'Get an instant estimate for your event', hi: 'अपने इवेंट का तुरंत अनुमान पाएं', ta: 'உங்கள் நிகழ்வுக்கான உடனடி மதிப்பீட்டைப் பெறுங்கள்' },
  'pricing.event_type': { en: 'Event Type', hi: 'इवेंट का प्रकार', ta: 'நிகழ்வு வகை' },
  'pricing.marriage': { en: 'Marriage', hi: 'शादी', ta: 'திருமணம்' },
  'pricing.birthday': { en: 'Birthday', hi: 'जन्मदिन', ta: 'பிறந்தநாள்' },
  'pricing.engagement': { en: 'Engagement / Reception', hi: 'सगाई / रिसेप्शन', ta: 'நிச்சயதார்த்தம் / வரவேற்பு' },
  'pricing.baby_shower': { en: 'Baby Shower / Seemantham', hi: 'गोद भराई / सीमंथम', ta: 'வளைகாப்பு / சீமந்தம்' },
  'pricing.house_warming': { en: 'House Warming', hi: 'गृह प्रवेश', ta: 'கிரகப்பிரவேசம்' },
  'pricing.corporate': { en: 'Corporate Event', hi: 'कॉर्पोरेट इवेंट', ta: 'நிறுவன நிகழ்வு' },
  'pricing.anniversary': { en: 'Anniversary', hi: 'सालगिरह', ta: 'ஆண்டுவிழா' },
  'pricing.puberty_ceremony': { en: 'Puberty Ceremony', hi: 'यौवन समारोह', ta: 'மஞ்சள் நீராட்டு' },
  'pricing.retirement': { en: 'Retirement Party', hi: 'सेवानिवृत्ति पार्टी', ta: 'ஓய்வு விழா' },
  'pricing.religious': { en: 'Religious / Pooja', hi: 'धार्मिक / पूजा', ta: 'மதம் / பூஜை' },
  'pricing.other_events': { en: 'Other Events', hi: 'अन्य इवेंट', ta: 'பிற நிகழ்வுகள்' },
  'pricing.marriage_type': { en: 'Marriage Type', hi: 'शादी का प्रकार', ta: 'திருமண வகை' },
  'pricing.event_date': { en: 'Event Date', hi: 'इवेंट की तारीख', ta: 'நிகழ்வு தேதி' },
  'pricing.guest_count': { en: 'Number of Guests', hi: 'मेहमानों की संख्या', ta: 'விருந்தினர் எண்ணிக்கை' },
  'pricing.get_pricing': { en: 'Get Pricing', hi: 'कीमत देखें', ta: 'விலை பெறு' },
  'pricing.your_estimate': { en: 'Your Estimate', hi: 'आपका अनुमान', ta: 'உங்கள் மதிப்பீடு' },
  'pricing.total': { en: 'Total', hi: 'कुल', ta: 'மொத்தம்' },
  'pricing.incl_platform_fees': { en: '*incl. platform fees', hi: '*प्लेटफ़ॉर्म शुल्क सहित', ta: '*தளக் கட்டணம் உட்பட' },
  'pricing.decoration': { en: 'Decoration', hi: 'सजावट', ta: 'அலங்காரம்' },
  'pricing.catering': { en: 'Catering', hi: 'कैटरिंग', ta: 'சமையல்' },
  'pricing.photography': { en: 'Photography', hi: 'फोटोग्राफी', ta: 'புகைப்படம்' },
  'pricing.dj_entertainment': { en: 'Celebrate With Music', hi: 'संगीत के साथ उत्सव मनाएँ', ta: 'இசையுடன் கொண்டாடுங்கள்' },
  'pricing.venue': { en: 'Venue', hi: 'स्थान', ta: 'அரங்கம்' },
  'pricing.professionals': { en: 'Professionals', hi: 'प्रोफेशनल', ta: 'நிபுணர்கள்' },
  'pricing.equipment': { en: 'Equipment Rentals', hi: 'उपकरण किराया', ta: 'உபகரண வாடகை' },
  'pricing.flowers': { en: 'Flowers & Pooja Items', hi: 'फूल और पूजा सामग्री', ta: 'பூக்கள் & பூஜை பொருட்கள்' },
  'pricing.save_package': { en: 'Save as Package', hi: 'पैकेज के रूप में सेव करें', ta: 'தொகுப்பாக சேமி' },
  'pricing.items_in_cart': { en: 'items in cart', hi: 'कार्ट में आइटम', ta: 'கூடையில் பொருட்கள்' },
  'pricing.cart_empty': { en: 'Your cart is empty', hi: 'आपका कार्ट खाली है', ta: 'உங்கள் கூடை காலியாக உள்ளது' },
  'pricing.select_services': { en: 'Select services from the left to build your estimate', hi: 'अनुमान बनाने के लिए बाईं ओर से सेवाएं चुनें', ta: 'மதிப்பீட்டை உருவாக்க இடதுபுறத்திலிருந்து சேவைகளைத் தேர்ந்தெடுக்கவும்' },
  'pricing.birthday_deco': { en: 'Birthday Decoration', hi: 'जन्मदिन सजावट', ta: 'பிறந்தநாள் அலங்காரம்' },
  'pricing.saved_packages': { en: 'Saved Packages', hi: 'सेव किए गए पैकेज', ta: 'சேமித்த தொகுப்புகள்' },
  'pricing.budget': { en: 'Budget', hi: 'बजट', ta: 'பட்ஜெட்' },
  'pricing.premium': { en: 'Premium', hi: 'प्रीमियम', ta: 'பிரீமியம்' },
  'pricing.luxury': { en: 'Luxury', hi: 'लक्ज़री', ta: 'ஆடம்பர' },
  'pricing.per_plate': { en: 'per plate', hi: 'प्रति प्लेट', ta: 'ஒரு தட்டுக்கு' },
  'pricing.guests': { en: 'guests', hi: 'मेहमान', ta: 'விருந்தினர்' },
  'pricing.phone_number': { en: 'Phone Number', hi: 'फोन नंबर', ta: 'போன் நம்பர்' },

  // Vendor Dashboard
  'vendor.your_services': { en: 'Your Services', hi: 'आपकी सेवाएं', ta: 'உங்கள் சேவைகள்' },
  'vendor.add_service': { en: 'Add Service', hi: 'सेवा जोड़ें', ta: 'சேவை சேர்' },
  'vendor.bookings': { en: 'Bookings', hi: 'बुकिंग', ta: 'முன்பதிவுகள்' },
  'vendor.calendar': { en: 'Calendar Availability', hi: 'कैलेंडर उपलब्धता', ta: 'நாட்காட்டி கிடைக்கும்' },
  'vendor.no_services': { en: 'No services added yet', hi: 'अभी तक कोई सेवा नहीं जोड़ी', ta: 'இன்னும் சேவைகள் சேர்க்கப்படவில்லை' },

  // Professional Dashboard
  'pro.invite_earn': { en: 'Invite & Earn', hi: 'इनवाइट करें और कमाएं', ta: 'அழைத்து சம்பாதி' },
  'pro.commission_on_every_booking': {
    en: "you will get commission on the invited account's every booking",
    hi: 'आप जिस अकाउंट को इनवाइट करेंगे, उसकी हर बुकिंग पर आपको कमीशन मिलेगा',
    ta: 'நீங்கள் அழைக்கும் கணக்கின் ஒவ்வொரு முன்பதிவிலும் கமிஷன் பெறுவீர்கள்',
  },
  'pro.your_invite_link': { en: 'Your Invite Link', hi: 'आपका इनवाइट लिंक', ta: 'உங்கள் அழைப்பு இணைப்பு' },
  'pro.earn_commission': { en: 'Earn {pct}% Commission', hi: '{pct}% कमीशन कमाएं', ta: '{pct}% கமிஷன் சம்பாதியுங்கள்' },
  'pro.commission_desc': { en: 'You earn {pct}% commission on every booking made by professionals you invite.', hi: 'आप अपने द्वारा इनवाइट किए गए प्रोफेशनल की हर बुकिंग पर {pct}% कमीशन कमाते हैं।', ta: 'நீங்கள் அழைக்கும் நிபுணர்களின் ஒவ்வொரு முன்பதிவிலும் {pct}% கமிஷன் பெறுவீர்கள்.' },
  'pro.target_info': { en: 'You must invite at least {count} professionals every {days} days to keep earning. If target not met, commission resets.', hi: 'कमाते रहने के लिए हर {days} दिनों में कम से कम {count} प्रोफेशनल को इनवाइट करें। लक्ष्य पूरा न होने पर कमीशन रीसेट हो जाएगा।', ta: 'தொடர்ந்து சம்பாதிக்க, ஒவ்வொரு {days} நாட்களுக்கும் குறைந்தது {count} நிபுணர்களை அழைக்கவும். இலக்கு எட்டாவிட்டால் கமிஷன் மீட்டமைக்கப்படும்.' },
  'pro.target_met': { en: 'Target Met', hi: 'लक्ष्य पूरा', ta: 'இலக்கு எட்டியது' },
  'pro.more_needed': { en: '{n} more needed', hi: '{n} और चाहिए', ta: '{n} மேலும் தேவை' },
  'pro.referral_stats': { en: 'Referral Stats', hi: 'रेफ़रल आंकड़े', ta: 'பரிந்துரை புள்ளிவிவரங்கள்' },
  'pro.invited': { en: 'Invited', hi: 'इनवाइट किए', ta: 'அழைக்கப்பட்டவை' },
  'pro.registered': { en: 'Registered', hi: 'पंजीकृत', ta: 'பதிவு செய்தவை' },
  'pro.booked': { en: 'Booked', hi: 'बुक हुए', ta: 'முன்பதிவு ஆனவை' },
  'pro.total_commission': { en: 'Total Commission Earned', hi: 'कुल कमीशन कमाई', ta: 'மொத்த கமிஷன் வருவாய்' },
  'pro.wallet': { en: 'Wallet', hi: 'वॉलेट', ta: 'பணப்பை' },
  'pro.available_balance': { en: 'Available Balance', hi: 'उपलब्ध शेष', ta: 'கிடைக்கும் இருப்பு' },
  'pro.withdraw': { en: 'Withdraw', hi: 'निकासी', ta: 'பணம் எடு' },
  'pro.your_referrals': { en: 'Your Referrals', hi: 'आपके रेफ़रल', ta: 'உங்கள் பரிந்துரைகள்' },
  'pro.withdrawal_history': { en: 'Withdrawal History', hi: 'निकासी इतिहास', ta: 'பணம் எடுத்த வரலாறு' },
  'pro.transaction_history': { en: 'Transaction History', hi: 'लेनदेन इतिहास', ta: 'பரிவர்த்தனை வரலாறு' },
  'pro.your_services': { en: 'Your Services', hi: 'आपकी सेवाएं', ta: 'உங்கள் சேவைகள்' },

  // Admin
  'admin.dashboard': { en: 'Admin Dashboard', hi: 'एडमिन डैशबोर्ड', ta: 'நிர்வாகி டாஷ்போர்டு' },
  'admin.statistics': { en: 'Statistics', hi: 'आंकड़े', ta: 'புள்ளிவிவரங்கள்' },
  'admin.vendors': { en: 'Vendors', hi: 'वेंडर', ta: 'விற்பனையாளர்கள்' },
  'admin.customers': { en: 'Customers', hi: 'कस्टमर', ta: 'வாடிக்கையாளர்கள்' },
  'admin.services': { en: 'Services', hi: 'सेवाएं', ta: 'சேவைகள்' },
  'admin.bookings': { en: 'Bookings', hi: 'बुकिंग', ta: 'முன்பதிவுகள்' },
  'admin.quick_add': { en: 'Quick Add', hi: 'क्विक एड', ta: 'விரைவு சேர்' },
  'admin.withdrawals': { en: 'Withdrawals', hi: 'निकासी', ta: 'பணம் எடுத்தல்' },
  'admin.referral_settings': { en: 'Referral Commission Settings', hi: 'रेफ़रल कमीशन सेटिंग्स', ta: 'பரிந்துரை கமிஷன் அமைப்புகள்' },
  'admin.commission_rate': { en: 'Commission Rate', hi: 'कमीशन दर', ta: 'கமிஷன் விகிதம்' },
  'admin.required_invites': { en: 'Required Invites', hi: 'आवश्यक इनवाइट', ta: 'தேவையான அழைப்புகள்' },
  'admin.period_days': { en: 'Period (Days)', hi: 'अवधि (दिन)', ta: 'காலம் (நாட்கள்)' },
  'admin.approve': { en: 'Approve', hi: 'स्वीकृत करें', ta: 'ஒப்புதல்' },
  'admin.reject': { en: 'Reject', hi: 'अस्वीकार करें', ta: 'நிராகரி' },
  'admin.total_vendors': { en: 'Total Vendors', hi: 'कुल वेंडर', ta: 'மொத்த விற்பனையாளர்கள்' },
  'admin.total_customers': { en: 'Total Customers', hi: 'कुल कस्टमर', ta: 'மொத்த வாடிக்கையாளர்கள்' },
  'admin.total_bookings': { en: 'Total Bookings', hi: 'कुल बुकिंग', ta: 'மொத்த முன்பதிவுகள்' },
  'admin.total_services': { en: 'Total Services', hi: 'कुल सेवाएं', ta: 'மொத்த சேவைகள்' },

  // Customer Dashboard
  'customer.your_bookings': { en: 'Your Bookings', hi: 'आपकी बुकिंग', ta: 'உங்கள் முன்பதிவுகள்' },
  'customer.manage_bookings': { en: 'Manage your event bookings', hi: 'अपनी इवेंट बुकिंग प्रबंधित करें', ta: 'உங்கள் நிகழ்வு முன்பதிவுகளை நிர்வகிக்கவும்' },
  'customer.no_bookings': { en: 'No bookings yet', hi: 'अभी तक कोई बुकिंग नहीं', ta: 'இன்னும் முன்பதிவுகள் இல்லை' },
  'customer.browse_first': { en: 'Browse vendors to make your first booking', hi: 'पहली बुकिंग के लिए वेंडर ब्राउज़ करें', ta: 'முதல் முன்பதிவு செய்ய விற்பனையாளர்களைப் பாருங்கள்' },
  'customer.total_price': { en: 'Total Price', hi: 'कुल कीमत', ta: 'மொத்த விலை' },

  // Vendor Listing
  'vendors.title': { en: 'Find Vendors', hi: 'वेंडर खोजें', ta: 'விற்பனையாளர்களைக் கண்டறி' },
  'vendors.all_categories': { en: 'All Categories', hi: 'सभी श्रेणियाँ', ta: 'அனைத்து வகைகள்' },
  'vendors.no_vendors': { en: 'No vendors found', hi: 'कोई वेंडर नहीं मिला', ta: 'விற்பனையாளர்கள் இல்லை' },
  'vendors.view_details': { en: 'View Details', hi: 'विवरण देखें', ta: 'விவரங்களைப் பார்' },
  'vendors.book_now': { en: 'Book Now', hi: 'अभी बुक करें', ta: 'இப்போதே முன்பதிவு செய்' },
};

export const LanguageProvider = ({ children }) => {
  const [lang, setLang] = useState(() => localStorage.getItem('eventbook_lang') || 'en');

  useEffect(() => {
    localStorage.setItem('eventbook_lang', lang);
    if (lang === 'ta') {
      // Slight defer so React has flushed any in-flight renders before we
      // start walking the DOM.
      const id = setTimeout(() => startTanglish(), 0);
      return () => { clearTimeout(id); stopTanglish(); };
    }
    // Non-Tamil — make sure no leftover transliteration is showing.
    stopTanglish();
  }, [lang]);

  const t = (key, params = {}) => {
    const entry = translations[key];
    if (!entry) {
      // Unknown key — fall back to transliteration so Tamil users never
      // see raw English text when the dictionary doesn't have a translation.
      const raw = key;
      let text = lang === 'ta' ? transliterateToTamil(raw) : raw;
      Object.keys(params).forEach(k => { text = text.replace(`{${k}}`, params[k]); });
      return text;
    }
    let text = entry[lang];
    if (!text) {
      // Translation missing for this language — for Tamil we transliterate
      // the English fallback instead of showing the English word raw.
      text = lang === 'ta' ? transliterateToTamil(entry['en'] || key) : (entry['en'] || key);
    }
    Object.keys(params).forEach(k => {
      text = text.replace(`{${k}}`, params[k]);
    });
    return text;
  };

  /**
   * tx(text) — transliterate any plain English string when the current
   * language is Tamil. Use this for runtime-generated labels (vendor
   * categories, profession names, server-driven strings) that aren't in
   * the translations dict. No-op for English / Hindi.
   *
   *   tx('Photographer')  // en → 'Photographer', ta → 'ஃபொடொக்ராஃபர்'
   */
  const tx = (text) => {
    if (text === null || text === undefined) return text;
    if (lang !== 'ta') return text;
    return transliterateToTamil(String(text));
  };

  return (
    <LanguageContext.Provider value={{ lang, setLang, t, tx, LANGUAGES }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => useContext(LanguageContext);
export { LANGUAGES };
